import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Briefcase, Mail, Phone, Download, Trash2, Filter, RefreshCw,
  CheckCircle, AlertCircle, FileText, Clock, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ['new', 'reviewed', 'shortlisted', 'rejected', 'hired'];

const STATUS_STYLES = {
  new: 'bg-[#F2B705]/15 text-[#F2B705] border-[#F2B705]/30',
  reviewed: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  shortlisted: 'bg-purple-500/10 text-purple-300 border-purple-500/30',
  rejected: 'bg-red-500/10 text-red-300 border-red-500/30',
  hired: 'bg-green-500/10 text-green-300 border-green-500/30',
};

function formatBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function ApplicationRow({ app, onSelect, isOpen, onChangeStatus, onDelete, downloadHref }) {
  return (
    <div
      data-testid={`application-row-${app.id}`}
      className={`bg-[#161616] border ${isOpen ? 'border-[#F2B705]/40' : 'border-white/10'} transition-colors`}
    >
      <button
        type="button"
        onClick={() => onSelect(app.id)}
        className="w-full p-5 flex items-center gap-4 text-left hover:bg-white/[0.02] transition-colors"
        data-testid={`application-toggle-${app.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap mb-1">
            <h3 className="text-sm font-semibold text-white truncate">{app.name}</h3>
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border ${STATUS_STYLES[app.status] || STATUS_STYLES.new}`}>
              {app.status || 'new'}
            </span>
          </div>
          <p className="text-xs text-[#A3A3A3] truncate">{app.job_title || 'General Application'}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
            <span className="text-[11px] text-[#666] flex items-center gap-1.5"><Mail size={10} />{app.email}</span>
            {app.phone && <span className="text-[11px] text-[#666] flex items-center gap-1.5"><Phone size={10} />{app.phone}</span>}
            <span className="text-[11px] text-[#666] flex items-center gap-1.5"><Clock size={10} />{formatDate(app.submitted_at)}</span>
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-white/5 p-5 space-y-5 bg-[#0E0E0E]">
          {app.message && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#F2B705] font-bold mb-2">Cover Note</p>
              <p className="text-sm text-[#A3A3A3] leading-relaxed whitespace-pre-wrap">{app.message}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            {app.resume && (
              <a
                href={downloadHref(app.id, 'resume')}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`download-resume-${app.id}`}
                className="border border-[#F2B705]/30 text-[#F2B705] text-xs uppercase tracking-wide font-bold px-4 py-2 hover:bg-[#F2B705] hover:text-black transition-all inline-flex items-center gap-2"
              >
                <Download size={12} /> Resume ({formatBytes(app.resume.size)})
              </a>
            )}
            {app.additional && (
              <a
                href={downloadHref(app.id, 'additional')}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`download-additional-${app.id}`}
                className="border border-white/10 text-[#A3A3A3] text-xs uppercase tracking-wide font-bold px-4 py-2 hover:border-white/30 hover:text-white transition-all inline-flex items-center gap-2"
              >
                <FileText size={12} /> Additional ({formatBytes(app.additional.size)})
              </a>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-white/5">
            <span className="text-[10px] uppercase tracking-widest text-[#888] font-bold">Status:</span>
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onChangeStatus(app.id, s)}
                data-testid={`status-${s}-${app.id}`}
                className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 border transition-colors ${
                  app.status === s
                    ? (STATUS_STYLES[s] || STATUS_STYLES.new)
                    : 'border-white/10 text-[#888] hover:text-white hover:border-white/30'
                }`}
              >
                {s}
              </button>
            ))}
            <button
              type="button"
              onClick={() => onDelete(app.id)}
              data-testid={`delete-application-${app.id}`}
              className="ml-auto text-[#666] hover:text-red-400 p-2"
              title="Delete application"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ApplicationsManager() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');

  const fetchApps = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = filter === 'all'
        ? `${API}/api/admin/applications`
        : `${API}/api/admin/applications?status=${filter}`;
      const res = await axios.get(url, { withCredentials: true });
      setApps(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const handleChangeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/api/admin/applications/${id}`, { status }, { withCredentials: true });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this application and its uploaded files?')) return;
    try {
      await axios.delete(`${API}/api/admin/applications/${id}`, { withCredentials: true });
      setApps((prev) => prev.filter((a) => a.id !== id));
      if (openId === id) setOpenId(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const downloadHref = (id, kind) => `${API}/api/admin/applications/${id}/file/${kind}`;

  const counts = useMemo(() => {
    const c = { all: apps.length };
    STATUSES.forEach((s) => { c[s] = apps.filter((a) => a.status === s).length; });
    return c;
  }, [apps]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3"><Briefcase size={20} className="text-[#F2B705]" /> Job Applications</h1>
          <p className="text-xs text-[#666] mt-1">Review applicants, download resumes, and update status.</p>
        </div>
        <button
          type="button"
          onClick={fetchApps}
          data-testid="applications-refresh-btn"
          className="border border-white/10 text-[#888] text-xs uppercase tracking-wide font-bold px-4 py-2 hover:text-white hover:border-white/30 transition-colors inline-flex items-center gap-2"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 mb-4 flex items-center gap-2">
          <AlertCircle size={14} />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="p-1"><X size={14} /></button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6 items-center">
        <Filter size={12} className="text-[#666]" />
        {['all', ...STATUSES].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            data-testid={`filter-${s}`}
            className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors ${
              filter === s
                ? 'bg-[#F2B705] text-black border-[#F2B705]'
                : 'border-white/10 text-[#888] hover:text-white'
            }`}
          >
            {s} <span className="text-[9px] ml-1 opacity-60">({counts[s] ?? 0})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-[#666]">Loading applications…</p>
      ) : apps.length === 0 ? (
        <div className="border border-white/10 bg-[#161616] p-10 text-center" data-testid="applications-empty">
          <CheckCircle className="text-[#666] mx-auto mb-3" size={28} />
          <p className="text-sm text-[#888]">No applications match this filter yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {apps.map((app) => (
            <ApplicationRow
              key={app.id}
              app={app}
              isOpen={openId === app.id}
              onSelect={(id) => setOpenId(openId === id ? null : id)}
              onChangeStatus={handleChangeStatus}
              onDelete={handleDelete}
              downloadHref={downloadHref}
            />
          ))}
        </div>
      )}
    </div>
  );
}
